<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Interpreter_Framework/The_Expressions_DSL';
    let nextLink= '/Documentation/Customizations';
    
    import Note from "$lib/notes/Note.svelte";
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="the-freon-interpreter-framework-1"
  bind:intersecting={visibleById["the-freon-interpreter-framework-1"]}
  onIntersectChange={(visible) => setVisible("the-freon-interpreter-framework-1", visible)}
>The Freon Interpreter Framework</SectionComponent>
<p>During Freon’s generation step, several files are produced that together form the foundation of an interpreter.
As a language engineer, the only file that you need to change is a file named <code>&lt;&lt;LanguageName&gt;&gt;Interpreter.ts</code>, where
<code>&lt;&lt;LanguageName&gt;&gt;</code> refers, of course, to the name of your DSL. In our <code>Expressions</code> example it is called
<code>ExpressionsInterpreter.ts</code>. </p>
<p>The class that is defined in this file inherits from the class <code>&lt;&lt;LanguageName&gt;&gt;InterpreterBase</code>,
which provides one evaluation function for every concept defined in your language. By overriding
these functions, you define the value associated with each AST node.. The following is an example of an evaluation function
in <code>ExpressionsInterpreterBase.ts</code>.</p>
<pre class="language-ts"><code class="language-ts">// Expressions/src/freon/interpreter/gen/ExpressionsInterpreterBase.ts#L35-L37

</code></pre>
<p>All evaluation functions are similar. The first parameter is the node for which a value needs to be determined.
The second parameter, of type <code>InterpreterContext</code>, provides
the <a
  href="/Documentation/Interpreter_Framework/Understanding_the_Framework#interpreter-context-3"
>context</a> in
which the expression should be evaluated.
This context is used to store values of constants, variables, etc. so they are available for
use in the function.
Initially, the base class functions throw an exception when called, as shown above, but when overridden,
they should result in an object of type <code>RtObject</code> (short for “runtime object”),
from <a
  href="/Documentation/Interpreter_Framework/Understanding_the_Framework#freons-runtime-object-library-2"
>Freon’s Runtime Object Library</a>.</p>
<p>Here is an example of how <code>evalNumberLiteralExpression</code> is overridden:</p>
<pre class="language-ts"><code class="language-ts">// Expressions/src/custom/interpreter/ExpressionsInterpreter.ts#L36-L38

override evalNumberLiteralExpression(node: NumberLiteralExpression, ctx: InterpreterContext): RtObject &#123;
    return new RtNumber(node.value);
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="freons-runtime-object-library-2"
  bind:intersecting={visibleById["freons-runtime-object-library-2"]}
  onIntersectChange={(visible) => setVisible("freons-runtime-object-library-2", visible)}
>Freon’s Runtime Object Library</SectionComponent>
<p>Each evaluation result in Freon is represented as an <code>RtObject</code> (or subclass),
ensuring a strict separation between the model (M1) and runtime (M0) levels.
There can, of course, be references from <code>RtObjects</code> to instances in the model, e.g. for traceability.
Freon provides a standard set of runtime classes ready for immediate use. These include:</p>
<ul>
<li><strong><code>RtNumber</code></strong>: Represents numeric values.</li>
<li><strong><code>RtString</code></strong>: Represents strings.</li>
<li><strong><code>RtBoolean</code></strong>: Represents boolean values.</li>
<li><strong><code>RtArray</code></strong>: Represents arrays.</li>
<li><strong><code>RtError</code></strong>: Represents errors.</li>
</ul>
<p>You can extend these foundational classes to create domain-specific runtime objects. </p>
<Note>{#snippet header()}Meta Levels{/snippet}
{#snippet content()}
Freon’s interpreter framework distinguishes three meta levels, similar to the standard M2/M1/M0 structure used in modeling.
<ol>
<li>The language definition, defining which concepts are available. Often called the M2 level.
In Freon this is represented by the language definition in the .ast files.
In Java this would be the Java Language Definition.
</li>   
<li>The model, which contains instances of the  language concepts, called the M1 level.
In Freon this is what you edit in a Freon application.
In Java this would be a Java program consisting of Java classes.
</li>
<li>The runtime level, values resulting from executing or interpreting the model,
called the M0 level.
In Freon this is the result of the interpreter running, or it would be the result of executing code generated from the model (M1) level.
In Java, this corresponds to the execution of a Java program.
</li>
</ol>
{/snippet}
</Note>
<SectionComponent tag="h2" 
  id="interpreter-context-3"
  bind:intersecting={visibleById["interpreter-context-3"]}
  onIntersectChange={(visible) => setVisible("interpreter-context-3", visible)}
>Interpreter Context</SectionComponent>
<p>Every AST node is evaluated within a certain context, represented by the <code>ctx: InterpreterContext</code> parameter.</p>
<p>For instance, if in the model we refer to a parameter in the body of a function, we need to know
the value of the parameter to be able to calculate the value of the body. This is achieved through the context as follows:</p>
<pre class="language-ts"><code class="language-ts">// Expressions/src/custom/interpreter/ExpressionsInterpreter.ts#L56-L65

override evalFunctionCallExpression(node: FunctionCallExpression, ctx: InterpreterContext): RtObject &#123;
    const calledFunction = node.$calledFunction;
    const functionContext = new InterpreterContext(ctx);
    node.arguments.forEach((arg, index) =&gt; &#123;
        const argumentValue = main.evaluate(arg, ctx);
        // Add the parameter to the context with the value of the evaluated argument
        functionContext.set(calledFunction.parameters[index], argumentValue);
    &#125;);
    return main.evaluate(calledFunction, functionContext);
&#125;</code></pre>
<p>The evaluation of the function call expression has two parts.
In the first part a new <code>InterpreterContext</code> is created with the original context as its parent.
This ensures that all values in the original context remain accessible.</p>
<p>Then all the arguments of the function call are evaluated and their value is stored in the context with the corresponding parameter as its key.</p>
<p>The function is then evaluated using <code>main.evaluate(calledFunction, functionContext)</code>, with the new context as its parameter.</p>
<p>If we come across a <code>ParameterRef</code> inside the evaluation of the function body,
this evaluation can simply lookup the value of the parameter:</p>
<pre class="language-ts"><code class="language-ts">// Expressions/src/custom/interpreter/ExpressionsInterpreter.ts#L71-L73

override evalParameterRef(node: ParameterRef, ctx: InterpreterContext): RtObject &#123;
    return ctx.find(node.$parameter);
&#125;</code></pre>
<p>Note that the value of the parameter lookup will be different for different calls to the function.
Which is exactly what we need.</p>
<SectionComponent tag="h2" 
  id="running-the-interpreter-4"
  bind:intersecting={visibleById["running-the-interpreter-4"]}
  onIntersectChange={(visible) => setVisible("running-the-interpreter-4", visible)}
>Running the Interpreter</SectionComponent>
<Note header={header2} content={content2}> 
{#snippet header2()}The selected node is the one that is interpreted{/snippet}
{#snippet content2()}
When you run the interpreter from the toolbar, it evaluates the currently selected node. 
You will see a different result in the Interpreter view for different nodes.
In many cases, the interpretation cannot be completed fully because additional context is required. 
It is the responsibility of the interpreter developer to provide this context.
{/snippet}
</Note>
<p>The following example shows the interpreter output for our Expressions language.
The model defines one function, <code>range</code>, and two expressions. When running the interpreter from the toolbar
in the Freon editor, a trace of the evaluation is shown in the Interpreter view.
In the trace you can find two <code>Function</code> evaluations. At the end of both lines<br>
the context is shown with different values for the <code>start</code> and <code>end</code> parameters
for each function call.</p>
<Figure
imageName={'interpreter/model-output.png'}
caption={'Expressions Model in the Editor'}
figureNumber={2}
/>


<PrevNextSection {prevLink} {nextLink} />